import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  MonitoringSession,
  MonitoringSessionDetail
} from '../../types/DeployerControlMonitoring.types';

type DeployerControlMonitoringState = {
  // Which AI system the cached sessions/detail below belong to. This component
  // stays mounted across route navigation (only its aiSystemId prop changes)
  // AND gets fully remounted every time its tab is switched away from and
  // back to (Tabs.tsx only renders the active tab's content). Tracking this
  // in the slice — which survives unmount — lets effects tell "genuinely a
  // different AI system" apart from "same system, just remounted", so a
  // plain tab switch shows the already-loaded data instantly instead of
  // blanking and re-fetching from the network every time.
  aiSystemId: string | null;
  sessions: MonitoringSession[];
  sessionsLoading: boolean;
  sessionsError: string | null;
  selectedSessionId: string | null;
  activeSessionDetail: MonitoringSessionDetail | null;
  activeSessionLoading: boolean;
  activeSessionError: string | null;
};

const initialState: DeployerControlMonitoringState = {
  aiSystemId: null,
  sessions: [],
  sessionsLoading: false,
  sessionsError: null,
  selectedSessionId: null,
  activeSessionDetail: null,
  activeSessionLoading: false,
  activeSessionError: null
};

const deployerControlMonitoringSlice = createSlice({
  name: 'deployerControlMonitoring',
  initialState,
  reducers: {
    setSessionsLoading: (state, action: PayloadAction<boolean>) => {
      state.sessionsLoading = action.payload;
    },
    setSessionsSuccess: (
      state,
      action: PayloadAction<{ aiSystemId: string; sessions: MonitoringSession[] }>
    ) => {
      state.aiSystemId = action.payload.aiSystemId;
      state.sessions = action.payload.sessions;
      state.sessionsLoading = false;
      state.sessionsError = null;
    },
    setSessionsError: (state, action: PayloadAction<string>) => {
      state.sessionsError = action.payload;
      state.sessionsLoading = false;
    },
    // Patches one session into the list by id — inserted at the front if new
    // (a freshly-created session), replaced in place if it already exists
    // (a polling refresh). Accepts either a bare MonitoringSession or the
    // fuller MonitoringSessionDetail (structurally a superset).
    upsertSession: (state, action: PayloadAction<MonitoringSession>) => {
      const index = state.sessions.findIndex((s) => s.id === action.payload.id);
      if (index === -1) {
        state.sessions.unshift(action.payload);
      } else {
        state.sessions[index] = { ...state.sessions[index], ...action.payload };
      }
    },
    selectSession: (state, action: PayloadAction<string | null>) => {
      state.selectedSessionId = action.payload;
    },
    setActiveSessionLoading: (state, action: PayloadAction<boolean>) => {
      state.activeSessionLoading = action.payload;
    },
    setActiveSessionSuccess: (
      state,
      action: PayloadAction<MonitoringSessionDetail>
    ) => {
      state.activeSessionDetail = action.payload;
      state.activeSessionLoading = false;
      state.activeSessionError = null;
    },
    setActiveSessionError: (state, action: PayloadAction<string>) => {
      state.activeSessionError = action.payload;
      state.activeSessionLoading = false;
    },
    resetDeployerControlMonitoring: () => initialState
  }
});

export const {
  setSessionsLoading,
  setSessionsSuccess,
  setSessionsError,
  upsertSession,
  selectSession,
  setActiveSessionLoading,
  setActiveSessionSuccess,
  setActiveSessionError,
  resetDeployerControlMonitoring
} = deployerControlMonitoringSlice.actions;

export default deployerControlMonitoringSlice.reducer;
